import { Helmet } from 'react-helmet-async'
import { useState, useEffect } from 'react'
import { db } from '../lib/supabase'

export default function Ubicanos() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [sliderImages, setSliderImages] = useState([])
  const [cards, setCards] = useState([])
  const [seoConfig, setSeoConfig] = useState(null)
  const [faqs, setFaqs] = useState([])
  const [toast, setToast] = useState(null) // { type: 'mailto'|'tel', address: string }
  
  useEffect(() => {
    const load = async () => {
      const { data: slides } = await db.getUbicanosSlider()
      const { data: cardsData } = await db.getUbicanosCards()
      const { data: seo } = await db.getSEOConfig('ubicanos')
      const { data: faqsData } = await db.getFAQsByScope('page', null, 'ubicanos')
      setSliderImages((slides || []).map(s => ({
        url: s.imagen_url,
        alt: s.alt_text || s.titulo || 'Foto'
      })))
      setCards(cardsData || [])
      setSeoConfig(seo || null)
      setFaqs(faqsData || [])
    }
    load()
  }, [])

  const nextSlide = () => {
    setCurrentSlide((prev) => sliderImages.length ? (prev + 1) % sliderImages.length : 0)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => sliderImages.length ? (prev - 1 + sliderImages.length) % sliderImages.length : 0)
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }
  return (
    <>
      <Helmet>
        <title>{seoConfig?.meta_title || 'Ubícanos - Plaza Aurora'}</title>
        <meta name="description" content={seoConfig?.meta_description || 'Encuentra Plaza Aurora Outlet en Lima Norte'} />
        <link rel="canonical" href={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/ubicanos`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_PE" />
        <meta property="og:site_name" content="Plaza Aurora" />
        <meta property="og:url" content={seoConfig?.canonical_url || `${import.meta.env.VITE_SITE_URL || 'https://plazaaurora.pe'}/ubicanos`} />
        <meta property="og:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Ubícanos - Plaza Aurora'} />
        <meta property="og:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Encuentra Plaza Aurora Outlet en Lima Norte'} />
        {seoConfig?.og_image_url && <meta property="og:image" content={seoConfig.og_image_url} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={seoConfig?.twitter_card || 'summary_large_image'} />
        <meta name="twitter:title" content={seoConfig?.og_title || seoConfig?.meta_title || 'Ubícanos - Plaza Aurora'} />
        <meta name="twitter:description" content={seoConfig?.og_description || seoConfig?.meta_description || 'Encuentra Plaza Aurora Outlet en Lima Norte'} />
        {seoConfig?.og_image_url && <meta name="twitter:image" content={seoConfig.og_image_url} />}
        
        {/* Robots */}
        <meta name="robots" content={seoConfig?.robots_index === false ? 'noindex,nofollow' : 'index,follow'} />
      </Helmet>
      
      {seoConfig?.json_ld && (
        <script type="application/ld+json">
          {seoConfig.json_ld}
        </script>
      )}
      
      {/* ShoppingCenter Schema - Always visible for GEO positioning */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ShoppingCenter",
          "name": "Plaza Aurora Outlet - Lima Norte",
          "url": "https://plazaaurora.pe/ubicanos",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Av. Carlos Izaguirre 271-275",
            "addressLocality": "Independencia",
            "addressRegion": "Lima",
            "postalCode": "15311",
            "addressCountry": "PE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": -11.99029,
            "longitude": -77.06213
          }
        })}
      </script>
      
      <main>
        <div className="container" style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem',
          paddingTop: '4.5rem'
        }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2.5rem, 9vw, 4.5rem)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: '0.5rem',
            letterSpacing: '0.02em'
          }}>
            Ubícanos
          </h1>
          
          <p style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-body)',
            color: 'var(--color-text-muted)',
            marginBottom: '3rem'
          }}>
            Visítanos en nuestro centro comercial
          </p>

          {/* Two Column Layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '1.5rem',
            alignItems: 'start'
          }}
          className="ubicanos-grid">
            {/* Left Column - Information Cards */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              {cards.length === 0 ? (
                <div style={{
                  background: 'var(--color-background)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1.5rem',
                  border: '1px solid var(--color-stroke)'
                }}>
                  <p style={{
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--font-size-body)',
                    color: 'var(--color-text-muted)'
                  }}>
                    No hay tarjetas configuradas aún.
                  </p>
                </div>
              ) : (
                cards.map((card) => (
                  <div key={card.id} style={{
                    background: 'var(--color-background)',
                    borderRadius: 'var(--radius-md)',
                    padding: '1.5rem',
                    border: '1px solid var(--color-stroke)'
                  }}>
                    <h2 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--font-size-h3)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--color-text-primary)',
                      marginBottom: '1rem',
                      letterSpacing: '0.02em'
                    }}>
                      {card.icono ? `${card.icono} ` : ''}{card.titulo.toUpperCase()}
                    </h2>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'pre-line'
                    }}>
                      {card.contenido}
                    </p>
                    {card.button_activo && card.button_url && (() => {
                      const url = card.button_url.trim()
                      const isInternal = url.startsWith('/')
                      const isWeb = /^https?:\/\//i.test(url)
                      const isProtocol = /^(mailto|tel):/i.test(url)

                      // Si no tiene protocolo ni es ruta interna, agregar https://
                      const href = (!isInternal && !isWeb && !isProtocol) ? `https://${url}` : url
                      const isExternal = !isInternal && !isProtocol

                      // Extraer dirección de protocolo para mostrar
                      const protoType = isProtocol ? url.match(/^(mailto|tel):/i)[1].toLowerCase() : null
                      const protoAddress = protoType ? url.replace(/^(mailto|tel):/i, '') : null

                      const handleProtocolClick = () => {
                        let appOpened = false
                        const onBlur = () => { appOpened = true }
                        window.addEventListener('blur', onBlur, { once: true })
                        setTimeout(() => {
                          window.removeEventListener('blur', onBlur)
                          if (!appOpened) {
                            setToast({ type: protoType, address: protoAddress })
                          }
                        }, 2000)
                      }

                      const btnStyle = {
                        display: 'inline-block',
                        marginTop: '0.75rem',
                        padding: '0.5rem 0.75rem',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--color-text-primary)',
                        color: 'var(--color-text-primary)',
                        textDecoration: 'none',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--font-size-small)',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease'
                      }

                      const btnHover = {
                        onMouseEnter: (e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)'
                          e.currentTarget.style.backgroundColor = 'var(--brand-blue-50)'
                          e.currentTarget.style.borderColor = 'var(--brand-blue)'
                          e.currentTarget.style.color = 'var(--brand-blue)'
                        },
                        onMouseLeave: (e) => {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.backgroundColor = 'transparent'
                          e.currentTarget.style.borderColor = 'var(--color-text-primary)'
                          e.currentTarget.style.color = 'var(--color-text-primary)'
                        },
                        onMouseDown: (e) => {
                          e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                          e.currentTarget.style.backgroundColor = 'var(--brand-blue-100)'
                        }
                      }

                      return (
                        <div>
                          <a
                            href={href}
                            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            {...(isProtocol ? { onClick: handleProtocolClick } : {})}
                            style={btnStyle}
                            {...btnHover}
                          >
                            {card.button_text || 'Abrir'}
                          </a>
                          {protoAddress && (
                            <div style={{
                              marginTop: '0.4rem',
                              fontFamily: 'var(--font-ui)',
                              fontSize: 'var(--font-size-micro)',
                              color: 'var(--color-text-muted)',
                              wordBreak: 'break-all'
                            }}>
                              {protoAddress}
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                ))
              )}
            </div>

            {/* Right Column - Photo Slider */}
            <div style={{
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-stroke)',
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
              aspectRatio: '1',
              maxWidth: '100%'
            }}>
              {/* Slider Track */}
              <div style={{
                display: 'flex',
                transition: 'transform 0.5s ease-in-out',
                height: '100%',
                transform: `translateX(-${currentSlide * 100}%)`
              }}>
                {sliderImages.length === 0 ? (
                  <div style={{
                    minWidth: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--color-accent-gray)'
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-muted)',
                      textAlign: 'center',
                      padding: '2rem'
                    }}>
                      Aún no hay imágenes en el slider
                    </p>
                  </div>
                ) : (
                  sliderImages.map((image, index) => (
                    <div
                      key={index}
                      style={{
                        minWidth: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--color-accent-gray)'
                      }}
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Navigation Buttons - Only show if there are images */}
              {sliderImages.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '1rem',
                      transform: 'translateY(-50%)',
                      background: 'rgba(54, 117, 207, 0.8)',
                      border: 'none',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: '#FFFFFF',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(54, 117, 207, 1)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(54, 117, 207, 0.8)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1)'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    }}
                  >
                    ‹
                  </button>

                  <button
                    onClick={nextSlide}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '1rem',
                      transform: 'translateY(-50%)',
                      background: 'rgba(54, 117, 207, 0.8)',
                      border: 'none',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: '#FFFFFF',
                      transition: 'all 0.2s ease',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(54, 117, 207, 1)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(54, 117, 207, 0.8)'
                      e.currentTarget.style.transform = 'translateY(-50%) scale(1)'
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                    }}
                  >
                    ›
                  </button>
                </>
              )}

              {/* Slide Indicators - Only show if there are images */}
              {sliderImages.length > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: '1rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: '8px',
                  zIndex: 10
                }}>
                  {sliderImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      style={{
                        width: currentSlide === index ? '24px' : '8px',
                        height: '8px',
                        borderRadius: currentSlide === index ? '4px' : '50%',
                        background: currentSlide === index ? 'var(--brand-blue)' : 'rgba(255, 255, 255, 0.5)',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (currentSlide !== index) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (currentSlide !== index) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Google Maps Section */}
          <div style={{
            marginTop: '3rem',
            marginBottom: '3rem'
          }}>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--font-size-h2)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              marginBottom: '1.5rem',
              letterSpacing: '0.02em',
              textAlign: 'center'
            }}>
              CÓMO LLEGAR
            </h2>
            
            <div style={{
              width: '100%',
              height: '500px',
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              border: '1px solid var(--color-stroke)',
              boxShadow: 'none'
            }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d15608.776!2d-77.06213!3d-11.99029!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x9105cef1f8f8f8f9%3A0x1234567890abcdef!2sAv.%20Carlos%20Izaguirre%20271-275%2C%20Independencia%2015311!5e0!3m2!1ses!2spe!4v1699999999999!5m2!1ses!2spe"
                width="100%"
                height="100%"
                style={{
                  border: 0,
                  display: 'block'
                }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación de Plaza Aurora Outlet - Av. Carlos Izaguirre 271-275, Independencia, Lima"
              />
            </div>
          </div>

          {/* FAQs Section */}
          {faqs.length > 0 && (
            <section id="faqs" role="region" aria-label="Preguntas Frecuentes" style={{ marginTop: '3rem', marginBottom: '3rem' }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-display-l)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                textAlign: 'center',
                marginBottom: '2rem'
              }}>PREGUNTAS FRECUENTES</h2>
              
              <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {faqs.map((faq) => (
                  <details key={faq.id} style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.5rem',
                    border: '1px solid var(--color-stroke)',
                    cursor: 'pointer'
                  }}>
                    <summary style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-h4)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--color-text-primary)',
                      listStyle: 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {faq.pregunta}
                      <span style={{ fontSize: '1.5rem', marginLeft: '1rem' }}>+</span>
                    </summary>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-body)',
                      color: 'var(--color-text-muted)',
                      lineHeight: '1.6',
                      marginTop: '1rem',
                      whiteSpace: 'pre-line'
                    }}>
                      {faq.respuesta}
                    </p>
                  </details>
                ))}
              </div>
              
              {/* FAQPage JSON-LD */}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  "mainEntity": faqs.map(faq => ({
                    "@type": "Question",
                    "name": faq.pregunta,
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": faq.respuesta
                    }
                  }))
                })}
              </script>
            </section>
          )}
        </div>

        {/* Toast para fallos de mailto:/tel: */}
        {toast && (
          <div style={{
            position: 'fixed',
            bottom: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: '420px',
            width: 'calc(100% - 2rem)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-stroke)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            padding: '1.25rem',
            zIndex: 9999,
            animation: 'toastSlideUp 0.3s ease'
          }}>
            <p style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-body)',
              color: 'var(--color-text-primary)',
              margin: '0 0 0.5rem 0',
              fontWeight: 'var(--font-weight-semibold)'
            }}>
              ⚠️ No se pudo abrir la aplicación{toast.type === 'mailto' ? ' de correo' : ' de teléfono'}
            </p>
            <p style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-small)',
              color: 'var(--color-text-muted)',
              margin: '0 0 0.75rem 0'
            }}>
              Parece que tu dispositivo no tiene una app configurada para enlaces {toast.type === 'mailto' ? 'mailto' : 'tel'}. Puedes contactar manualmente a:
            </p>
            <div style={{
              background: 'var(--color-accent-gray)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.5rem 0.75rem',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-body)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              wordBreak: 'break-all',
              marginBottom: '0.75rem'
            }}>
              {toast.address}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(toast.address)
                  setToast(null)
                }}
                style={{
                  padding: '0.4rem 0.75rem',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-stroke)',
                  background: 'var(--color-surface)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-small)',
                  color: 'var(--color-text-primary)'
                }}
              >
                Copiar
              </button>
              <button
                onClick={() => setToast(null)}
                style={{
                  padding: '0.4rem 0.75rem',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'var(--brand-blue)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 'var(--font-size-small)',
                  color: 'white'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes toastSlideUp {
            from { opacity: 0; transform: translateX(-50%) translateY(16px); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
        `}</style>
      </main>
    </>
  )
}




